import { getWorkflow } from '@jira/project-settings__workflow-services-document/src/getApiPrefix.tsx';
import type { ApiGetWorkflowResponseWithUsages } from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import { createResourceError } from '@jira/router-resources__utils/src/createResourceError.tsx';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
// eslint-disable-next-line jira/import-whitelist
import {
	prefetchApiReadyMark,
	prefetchApiStartMark,
} from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import type { ResourceStoreContext, RouterDataContext } from '@atlassian/react-resource-router';

export const fetchWorkflowDocument = async (
	routerContext: RouterDataContext,
	{ tenantContext }: ResourceStoreContext,
	isProjectScoped: boolean,
): Promise<ApiGetWorkflowResponseWithUsages> => {
	const { isAnonymous, cloudId } = tenantContext;
	const { match } = routerContext;

	if (isAnonymous) {
		throw new Error("Workflows can't be edited in anonymous mode.");
	}
	if (!match.params.workflowId && !match.params.projectKey && !match.params.issueTypeId) {
		throw new Error('Workflow ID, project Key, or issue type ID is not defined.');
	}

	try {
		setMark(prefetchApiStartMark);

		let workflowDocument: ApiGetWorkflowResponseWithUsages | null = null;
		if (match.params.workflowId) {
			workflowDocument = await getWorkflow(
				cloudId,
				{
					workflowId: match.params.workflowId,
				},
				{ checkForDrafts: !isProjectScoped },
			);
		} else if (match.params.projectKey && match.params.issueTypeId) {
			const workflowIdentifier = {
				projectKeyAndIssueType: {
					projectKey: match.params.projectKey,
					issueTypeId: match.params.issueTypeId,
				},
			};

			workflowDocument = await getWorkflow(cloudId, workflowIdentifier, {
				checkForDrafts: !isProjectScoped,
			});
		} else {
			throw new Error('Project workflow context is not defined.');
		}

		if (!workflowDocument) {
			throw new Error('Workflow ID, project ID, or issue type ID is not defined.');
		}

		setMark(prefetchApiReadyMark);
		return workflowDocument;
	} catch (error: unknown) {
		throw createResourceError(
			{
				component: 'spa.view.routes.admin-pages.workflows.workflow-document.getData',
				message: 'An error was thrown while prefetching workflow document data',
				name: 'ResourceError',
			},
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any, typescript/consistent-type-assertions
			error as any,
		);
	}
};
